import { Injectable, signal } from '@angular/core';
import { collection, doc, DocumentData, Firestore, getDoc, getDocs, query, QuerySnapshot, where } from '@angular/fire/firestore';
import { Data } from '@angular/router';
import { CatchErrorService } from '@core/catch-error.service';
import { formatDisplayCurrencyAmount } from '@core/currency-display.util';
import { BehaviorSubject, catchError, defer, from, map, Observable, of, shareReplay, switchMap, throwError } from 'rxjs';
import { FirestoreDataService } from './firestore-data.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentDataService {

  checkoutDetails$: BehaviorSubject<Data> = new BehaviorSubject({});
  isDirectCurrencyConversion = signal<boolean>(false);
  
  constructor(
    private firestore: Firestore,
    private catchService: CatchErrorService,
    private firestoreDataService: FirestoreDataService,
  ) { }

  /**
   * This function returns a list of the user's saved cards
   *
   * @param userId the id of the user
   * @return a list of the user's saved cards
   */
  getUserSavedCardDetails(userId: string) {
    return this.catchService.isOnline$.pipe(
      switchMap(async (isOnline) => {
        if (!isOnline) {
          return null; // Return null if offline
        }

        const storedCardCollection = collection(this.firestore, `users/${userId}/stored_card`); // Reference collection
        const snapshot: QuerySnapshot<DocumentData> = await getDocs(storedCardCollection); // Fetch QuerySnapshot
        return snapshot; // Return the entire QuerySnapshot<DocumentData>
      }),
      map((snapshot) => {
        if (!snapshot) {
          return []; // Return an empty array in case of offline or no data
        }
        return this.firestoreDataService.snapShotData(snapshot); // Pass the QuerySnapshot directly to snapShotData
      }),
      shareReplay(1) // Cache results for reuse
    );
  }

  /**
     * The function `getLotteryDetails` retrieves the details of a lottery based on the provided product
     * ID.
     * @param {number} productid - The `productid` parameter is a number that represents the ID of a
     * product.
     * @returns an Observable that emits the data of the first document in the "lotteries" collection where
     * the "product_id" field contains the specified productid.
     */

  getLotteryDetails(productid: number) {
    try {
      const lotteriesRef = query(
        collection(this.firestore, '/lotteries'), // Firestore reference to "lotteries" collection
        where("product_id", "array-contains", productid) // Apply filter using "where"
      );

      // Convert Firestore's `getDocs()` result to an Observable
      return from(getDocs(lotteriesRef)).pipe(
        map((res: any) => res?.docs[0]?.data()), // Access the first document's data
        catchError((err) => {
          console.error('Error fetching lottery details:', err); // Log error for debugging
          return throwError(err); // Handle errors gracefully
        })
      );
    } catch (error) {
      console.error('Error in getLotteryDetails method:', error); // Log error
      return throwError(error); // Return error as an Observable
    }
  }

  getCreditTopupOfferDetail() {
    return this.firestoreDataService.getAngularFireStore(`/Global_lookup/credit_top_up/`, "snapshotChanges").pipe(
      map((res: Data) => res?.payload?.data()),
      shareReplay(1)
    );
  }

  /**
   * The function `getPaymentPageData` retrieves data from the Angular Firestore based on the provided
   * `prosub_id` and returns it as an observable.
   * @param prosub_id - The `prosub_id` parameter is the identifier for a specific payment page. It is
   * used to retrieve the details of that payment page from the Angular Firestore database.
   * @returns The function `getPaymentPageData` is returning an Observable that emits the data from the
   * Firestore document located at `/general_modules/prosub_id//details`. The data is
   * retrieved using the `snapshotChanges` method and then mapped to extract the payload data. The
   * `shareReplay(1)` operator is used to cache the emitted data and replay it to any subsequent
   * subscribers.
   */

public getPaymentPageData(prosub_id: string): Observable<Data> {
  const id = prosub_id || localStorage.getItem("prosub_id");
  if (!id) {
    return throwError(() => new Error("prosub_id missing."));
  }
  const docRef = doc(this.firestore, `/general_modules/prosub_id/${id}/details`);

  return from(getDoc(docRef)).pipe(
    switchMap((snapshot) => {
      const data = snapshot.data();
      if (!data) return of(null);
      return this.getPTypeByProsubId(id).pipe(
        map((p_type) => ({
          ...data,
          p_type
        })),
        catchError(() =>
          of({
            ...data,
            p_type: null
          })
        )
      );
    }),
    catchError((err) => {
      this.catchService.handleFirestoreError(`[PaymentPageData] Error fetching prosub_id ${prosub_id}: ${err}`);
      return of(null);
    }),
    shareReplay(1)
  );
}
public getPTypeByProsubId(prosub_id: string): Observable<string | null> {
  const rawId = prosub_id || localStorage.getItem("prosub_id");

  const id = String(rawId).trim(); 
  if (!id) {
    return throwError(() => new Error("prosub_id missing."));
  }

  const collectionRef = collection(this.firestore, '/general_modules/p_type/list');
  const q = query(collectionRef, where('prosubs', 'array-contains', id));

  return from(getDocs(q)).pipe(
    map((querySnapshot) => {
      if (querySnapshot.empty) {
        return null;
      }
      const docId = querySnapshot.docs[0].id;
      return docId;
    }),
    catchError((err) => {
      this.catchService.handleFirestoreError(`[PTypeLookup] Error for prosub_id ${id}: ${err}`);
      return of(null);
    }),
    shareReplay(1)
  );
}
  getCheckoutDetails(): void {
     defer(() => {
      const docRef = doc(this.firestore, '/general_modules/p_type/list/21');
      return from(getDoc(docRef)).pipe(
        map((docSnap) => {
          if (docSnap.exists()) {
            return docSnap.data();
          } else {
            throw new Error('Document does not exist');
          }
        }),
        catchError((err) => {
          console.error('Firestore getDoc error:', err);
          return of(null); // handle appropriately
        })
      );
    }).subscribe((value) => {
      this.checkoutDetails$.next(value);
    });
  }

  getBeautifiedCurrency(actualAmt, preferredCurrency, beautifyType?: string) {
    return new Promise((resolve, reject) => {
      try {
        let beautifiedAmt: number | string = 0;

        if (actualAmt >= 0.01 && actualAmt <= 0.09) {
          if (beautifyType === "currency_actual") {
            actualAmt = Number(actualAmt);
          } else {
            actualAmt = Number(actualAmt)?.toFixed(2);
          }
        } else if (actualAmt >= 0.1 && actualAmt <= 2.9) {
          let actualAmount: string | number;
          if (beautifyType === "currency_actual") {
            actualAmount = Number(actualAmt);
          } else {
            actualAmount = Number(actualAmt)?.toFixed(1); // string
          }


          let result: string[];
          if (typeof actualAmount === "string") {
            result = actualAmount.split(".");
          } else {
            result = String(actualAmount).split(".");
          }

          const zeroNum = 0;
          if (result[1] === "0") {
            actualAmt = Number(result[zeroNum]);
          } else {
            actualAmt = actualAmount;
          }

        } else {
          if (actualAmt === "7.20") {
            actualAmt = actualAmt;
          } else {
            if (beautifyType === "currency_actual") {
              actualAmt = Number(actualAmt);
            } else {
              actualAmt = Math.round(actualAmt);
            }
          }
        }

        this.getCurrencyConversion()?.subscribe((currencyJSON: Data) => {
          if (currencyJSON) {
            let getBeautify: Data = currencyJSON?.currency?.beautify;
            let getActualRate: number = currencyJSON?.currency?.conversion_rate?.[preferredCurrency];

            const currency_conversion = Number(getActualRate ? Number(actualAmt) * getActualRate : Number(actualAmt));

            if (this.isDirectCurrencyConversion()) {
              beautifiedAmt = currency_conversion?.toFixed(2);
            } else {
              if (beautifyType === "currency_actual") {
                beautifiedAmt = currency_conversion;
              } else if (getBeautify && getBeautify[actualAmt]) {
                beautifiedAmt = getBeautify?.[actualAmt]?.[preferredCurrency]?.toFixed(2);
              } else {
                beautifiedAmt = currency_conversion?.toFixed(2);
              }
            }
            if (beautifiedAmt) {
              resolve(formatDisplayCurrencyAmount(beautifiedAmt));
            } else {
              resolve(0);
            }
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }


  // Get currency conversion from Firestore
  getCurrencyConversion() {
    return this.firestoreDataService
      .getAngularFireStore(`/general_modules/currency_conversion`, "snapshotChanges")
      .pipe(
        map((res: Data) => {
          const data = res?.data?.();
          return data ?? null;
        }),
        catchError(() => {
          return of(null);
        })
      );
  }
}
